/* cali cart sync (2026-08-16, slimmed 2026-09-19)
 *
 * One reader of /cart.js for the whole cart footer. It listens to every way the
 * cart can change - Motion's own events plus a fetch/XHR net that catches the
 * app-driven mutations (Bag Builder, Selleasy, line removals) which fire no
 * theme event at all - debounces them, fetches the cart once, and:
 *
 *   1. rewrites "You're saving X" / "You're saving X in total", which Liquid
 *      renders outside Motion's ajax-refresh regions and would otherwise never
 *      update;
 *   2. broadcasts the fresh cart as `cali:cart`.
 *
 * WHAT WENT AWAY ON 2026-09-19 (the vendor did)
 * ---------------------------------------------
 * Until the in-house shipping-protection widget replaced Navidium ("Averra"),
 * this file also re-ran the vendor's `nvd_init`, swapped the in-cart protection
 * variant to whatever tier their code had just written into
 * localStorage.nvdVariant, de-duplicated their double add (their jQuery `.one()`
 * checkout handler fired once per matching ancestor of the click target), and
 * waited 1.2 s on load so their init could go first. All four existed to
 * contain someone else's code. With assets/cali-shipping-protection.js as the
 * single writer they have nothing left to contain and are gone; the initial
 * pass now runs immediately.
 *
 * This file no longer writes to the cart at all. It reads and it announces.
 */
(function () {
  'use strict';

  if (window.__caliCartSync) return;
  window.__caliCartSync = true;

  var PROTECTION_HANDLE = 'navidium-shipping-protection';
  var DEBOUNCE_MS = 600;
  var CART_MUTATION = /\/cart\/(add|change|update|clear)/;

  var scriptEl = document.getElementById('CaliCartSync') || document.currentScript;
  var moneySample = (scriptEl && scriptEl.getAttribute('data-money-sample')) || '';

  /* ---- i18n ----
     The same sentence is rendered by Liquid in snippets/cart-drawer.liquid and
     sections/main-cart.liquid from the locale keys
     bag_builder.cart_savings_prefix / bag_builder.cart_savings_total_suffix.
     Read the SAME two values off the tag so this rewrite cannot revert a
     translated cart to English. Falls back to today's English when the
     attribute is absent or the key is unregistered. */
  function attrStr(name, fallback) {
    var v = scriptEl && scriptEl.getAttribute(name);
    if (!v || v.indexOf('Translation missing') !== -1) return fallback;
    return v;
  }
  var savingsPrefix = attrStr('data-savings-prefix', "You're saving ");
  var savingsTotalSuffix = attrStr('data-savings-total-suffix', ' in total');

  /* ---- money formatting ----
     The snippet embeds a server-rendered sample of 123456789 cents
     (1,234,567.89 units) in the visitor's presentment currency. Parsing it
     recovers prefix, suffix, thousands and decimal separators, so output
     matches Liquid's `| money` exactly for every market. Exposed on
     window.caliCartSync because the shipping-protection fee label has to print
     the same money as the Liquid that painted it. */
  var moneyPattern = null;
  (function () {
    if (!moneySample) return;
    var m = moneySample.match(/1(\D*)234\D*56[78](\D*89)?/);
    if (!m) return;
    moneyPattern = {
      prefix: moneySample.slice(0, m.index),
      suffix: moneySample.slice(m.index + m[0].length),
      thousands: m[1] || '',
      decimal: m[2] ? m[2].slice(0, -2) : null
    };
  })();

  function groupDigits(intStr, sep) {
    return sep ? intStr.replace(/\B(?=(\d{3})+(?!\d))/g, sep) : intStr;
  }

  function formatCents(cents) {
    cents = Math.abs(Math.round(cents));
    var p = moneyPattern;
    if (p) {
      if (p.decimal === null) {
        return p.prefix + groupDigits(String(Math.round(cents / 100)), p.thousands) + p.suffix;
      }
      var intPart = String(Math.floor(cents / 100));
      var frac = String(cents % 100);
      if (frac.length < 2) frac = '0' + frac;
      return p.prefix + groupDigits(intPart, p.thousands) + p.decimal + frac + p.suffix;
    }
    try {
      if (window.theme && theme.Currency && theme.Currency.formatMoney) {
        return theme.Currency.formatMoney(cents, theme.settings && theme.settings.moneyFormat);
      }
    } catch (err) {}
    return (cents / 100).toFixed(2);
  }

  /* ---- cart helpers ---- */
  function getCart() {
    return fetch('/cart.js', { headers: { Accept: 'application/json' } }).then(function (r) {
      return r.json();
    });
  }

  function isProtection(item) {
    return !!(item && item.handle && item.handle.indexOf(PROTECTION_HANDLE) !== -1);
  }

  /* ---- "You're saving X" ----
     Mirrors the Liquid in cart-drawer.liquid and main-cart.liquid:
     sum of _saved_price * quantity over lines carrying the property.
     Drawer shows it only with 2+ saved lines (a single built bag already
     has its own per-item pill); the cart page shows it whenever > 0. */
  function updateSavings(cart) {
    var cents = 0;
    var lines = 0;
    (cart.items || []).forEach(function (it) {
      if (isProtection(it)) return;
      var saved = it.properties && parseFloat(it.properties._saved_price);
      if (saved > 0) {
        cents += Math.round(saved * 100) * it.quantity;
        lines += 1;
      }
    });

    var wrappers = document.querySelectorAll('.cart__price-wrapper');
    Array.prototype.forEach.call(wrappers, function (wrap) {
      if (!wrap.querySelector('[data-subtotal]')) return;
      var inDrawer = !!(wrap.closest && wrap.closest('#CartDrawer'));
      var show = cents > 0 && (inDrawer ? lines > 1 : true);
      var el = wrap.querySelector('.cart__savings-text');
      if (!show) {
        if (el && el.parentNode) el.parentNode.removeChild(el);
        return;
      }
      if (!el) {
        el = document.createElement('div');
        el.className = 'cart__savings-text';
        wrap.appendChild(el);
      }
      var text = savingsPrefix + formatCents(cents) + (inDrawer ? savingsTotalSuffix : '');
      if (el.textContent !== text) el.textContent = text;
    });
  }

  /* ---- pipeline ---- */
  var syncTimer = null;

  function schedule() {
    if (syncTimer) clearTimeout(syncTimer);
    syncTimer = setTimeout(runSync, DEBOUNCE_MS);
  }

  function announce(cart) {
    try {
      document.dispatchEvent(new CustomEvent('cali:cart', { detail: { cart: cart } }));
    } catch (err) {}
  }

  function runSync() {
    syncTimer = null;
    getCart()
      .then(function (cart) {
        if (!cart || !cart.items) return;
        updateSavings(cart);
        announce(cart);
      })
      .catch(function () {});
  }

  /* ---- triggers ----
     Motion's own events plus a fetch/XHR net for app-driven mutations
     (same pattern as cali-shipping-bar.js; both patches chain safely).
     Our own protection writes go through this net too, which is how the widget
     sees the result of its write and settles. */
  document.addEventListener('cart:updated', schedule);
  document.addEventListener('ajaxProduct:added', schedule);
  document.addEventListener('cart:open', schedule);

  var origFetch = window.fetch;
  if (origFetch) {
    window.fetch = function (input, init) {
      var url = typeof input === 'string' ? input : (input && input.url) || '';
      var promise = origFetch.apply(this, arguments);
      if (CART_MUTATION.test(url)) {
        promise.then(schedule, function () {});
      }
      return promise;
    };
  }

  var origOpen = XMLHttpRequest.prototype.open;
  XMLHttpRequest.prototype.open = function (method, url) {
    if (url && CART_MUTATION.test(String(url))) {
      this.addEventListener('load', schedule);
    }
    return origOpen.apply(this, arguments);
  };

  window.caliCartSync = {
    formatCents: formatCents,
    getCart: getCart,
    isProtection: isProtection,
    refresh: schedule
  };

  /* Initial pass. Heals a cart persisted from a previous session whose
     protection tier no longer matches its contents, and gives the widget its
     first `cali:cart` without a second /cart.js fetch of its own. */
  runSync();
})();
